import React, { useEffect, useState, useCallback } from 'react'
import { observer } from "mobx-react-lite"
import { useNavigate } from "@reach/router"

import Layout from '../layout/Layout'
import styles from './Pack.module.scss'

import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { TipsProvider } from '../loops/ctx'
import PackLoops from './PackLoops'
import MoreDetail from './MoreDetail'
import useWindowSize from '../../hooks/window-size';

import NewLoopIcon from './NewLoop'

import { usePack } from './PackAPI'
import usePackPlayer from '../common/usePackPlayer'
import { PackLoopsModel } from './model'

import axios from 'axios'
import useTitle from '../../hooks/title'
import useLocalStorage from '../../hooks/local-storage'
import Video, {useVideo} from '../help/video';
import userStore from '../common/userStore'
import { Tooltip } from '@mui/material'
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';


const usePackDate = (id) => {
  const { value, status, error } = usePack(id)
  if (value == null || value.length == 0) {
    return {
      loading: status !== 'success',
      pack: {},
      error
    }
  }
  return {
    loading: status !== 'success',
    pack: status === 'success' ? value[0] : {},
    error
  }
}

const DemoPanel = ({ pack }) => {
  const { isPlaying, playPack } = usePackPlayer()
  const navigate = useNavigate()
  const {width} = useWindowSize();

  const backImageStyle = pack.cover ? {backgroundImage: `url(${pack.cover})`} : {}

  const icon = isPlaying(pack) ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />

  return <div className={styles.demo}>
    <div className={styles.image} style={backImageStyle}></div>
    <div className={styles.info} >
      <div style={{display: 'flex'}}>
        <Typography variant="h3" className={styles.title}>{pack.title}</Typography>
        {pack.midi ? <Tooltip title='MIDI Download Available' placement='top'><div className={styles.midi}>MIDI</div></Tooltip> : ''}

      </div>

      <div className={styles.tags}><Chip className={styles.tag} size="small" variant="outlined" color="primary" label={pack.tag} /></div>

      <MoreDetail pack={pack} />

      <div className={styles.actions}>
        <div>
          <Button variant="contained"  
            size="large" color="primary" 
            className={styles['play-demo']}
            startIcon={icon}
            classes={{startIcon: styles['play-icon']}}
            onClick={() => playPack(pack)}
            >Play Demo</Button>
          {width > 700 && pack.presets ? <Button variant="contained"  
            size="large" color="primary" 
            className={styles['play-demo']}
            style={{marginLeft: 20}}
            startIcon={<DownloadForOfflineOutlinedIcon />}
            classes={{startIcon: styles['play-icon']}}
            onClick={() => navigate(pack.presets)}
            >Synth Presets</Button> : ''}
        </div>
        <div className={styles.right}>
          <div className={styles['mun-info']}>{pack.grooveCount} Grooves</div>
          {pack.lickCount > 0 ? <div className={styles['mun-info']}>{pack.lickCount} Riffs</div> : ''}
        </div>
      </div>
    </div>
  </div>
}

const Pack = observer(({packId}) => {
  const navigate = useNavigate()
  const { pack, loading, error } = usePackDate(packId)
  const [packConfig, setPackConfig] = useState(null)
  const [loopsModel, ] = useState(() => new PackLoopsModel({packId}))
  const backImageStyle = pack && pack.banner ? {backgroundImage: `url(${pack.banner})`} : {}
  useTitle(pack.title)
  const [playVideo, setPlayVideo] = useLocalStorage('playVideo', '0')
  const videoModel = useVideo();

  const [padding, setPadding] = useState(100)
  const handlePosition = useCallback(({top}) => {
    const height = window.innerHeight;
    const pad = height - top - 30;
    setPadding(pad > 0 ? pad : 0)
  }, [setPadding])

  useEffect(() => {
    if (pack && pack.config) {
      axios.get(pack.config).then((response) => {
        setPackConfig(response.data)
      })
    }
  }, [pack])

  useEffect(() => {
    loopsModel.setPackConfig(packConfig)
  }, [packConfig, loopsModel])

  useEffect(() => {
    loopsModel.setPack(pack)
  }, [loopsModel, pack])

  useEffect(() => {
    if (!pack.id) {
      return;
    }
    loopsModel.setPackId(pack.id)
    loopsModel.load()

    window.addEventListener("beforeunload", loopsModel.commitDeleted)

    return () => {
      window.removeEventListener("beforeunload", loopsModel.commitDeleted)
      loopsModel.commitDeleted()
    }
  }, [loopsModel, pack])

  useEffect(() => {
    if (error) {
      navigate('/404', {replace: true})
    }
  }, [error])

  useEffect(() => {
    if (loading || playVideo == '1' || userStore.isAnonymous) {
      return;
    }
    videoModel.show()
    setPlayVideo('1')
  }, [loading, playVideo, setPlayVideo, videoModel])

  if (loading) {
    return <Layout></Layout>
  }

  return <Layout>
    <TipsProvider requiredTips={!videoModel.open}>
    <div className={styles.root} style={{paddingBottom: padding}}>
      <div className={styles.topbg} style={backImageStyle}>
        <div className={styles.topbg2}></div>
      </div>
      <DemoPanel pack={ pack } />
      <PackLoops model={loopsModel}/>
      <NewLoopIcon className={styles.newLoops} pack={pack} config={packConfig} 
          handlePosition={handlePosition} loopsModel={loopsModel} />
    </div>
    <Video open={videoModel.open} onClose={videoModel.hide} />
    </TipsProvider>
  </Layout>
})

export default Pack